import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isNumber } from "lodash";
import classnames from "classnames";

import Tooltip from "../../../../components/Tooltip";
import Grid from "../../../../components/Grid";
import { Col, Row } from "../../../../components/Grid";
import { TransferStatsProps } from "./types";
import {
  BYTES_IN_GB,
  MILLISECOND_IN_MINUTE,
} from "../../../../utils/common/contants";
import { getMonthDayYearDate } from "../../../../utils/common";
import Switch from "../../../../components/Switch";
import ButtonText from "../../../../components/ButtonText";
import { round } from "../../../../utils/common";
import Icon from "../../../../components/Icon";
import { IconName } from "../../../../components/Icon/types";

import "./styles.scss";

export const TransferStats: FC<TransferStatsProps> = ({
  nextRotation,
  lifetimeTransfer,
  currentTransfer,
  isAutoRecycleActive,
  isFetchingSubscriptionOptions,
  onRecycle,
  onAutoRecycle,
}) => {
  const { t } = useTranslation();

  const transferLimitUnlimited = useMemo(() => {
    return currentTransfer?.quota === 0;
  }, [currentTransfer]);
  const isGB = useMemo(() => {
    return (
      currentTransfer &&
      isNumber(currentTransfer?.quota) &&
      currentTransfer?.quota > 0
    );
  }, [currentTransfer]);

  const isNan = useMemo(() => {
    return !transferLimitUnlimited && !isGB;
  }, [transferLimitUnlimited, isGB]);

  const [isAutorecycle, setIsAutorecycle] = useState(isAutoRecycleActive);

  const isManualRecycleAvailable = useMemo(() => {
    return (
      !isAutorecycle &&
      isGB &&
      currentTransfer &&
      currentTransfer.quota <= currentTransfer.amount
    );
  }, [isAutorecycle, isGB, currentTransfer]);

  const isManualRecycleDisabled = useMemo(() => {
    if (!nextRotation) {
      return true;
    }
    // Date after manual recycle returned as now + few seconds
    return (
      new Date(nextRotation).getTime() <=
      new Date().getTime() + 2 * MILLISECOND_IN_MINUTE
    );
  }, [nextRotation]);

  useEffect(() => {
    setIsAutorecycle(isAutoRecycleActive);
  }, [isAutoRecycleActive, isFetchingSubscriptionOptions]);

  const quotaInGB = useMemo(() => {
    if (isNan) {
      return 0;
    }

    return round((currentTransfer?.quota as number) / BYTES_IN_GB, 2);
  }, [currentTransfer, isNan]);

  const amountInGB = useMemo(() => {
    if (isNan) {
      return 0;
    }

    return round((currentTransfer?.amount as number) / BYTES_IN_GB, 2);
  }, [currentTransfer, isNan]);

  const handleChangeAutoRecycle = () => {
    setIsAutorecycle((isActive: boolean) => {
      onAutoRecycle(!isActive);
      return !isActive;
    });
  };

  return (
    <Grid className="ams-transfer-stats">
      <Row className="mt-m mb-m">
        <Col className="ams-transfer-stats__key">{t("transferLimit")}</Col>
        <Col className="ams-col__value">
          {transferLimitUnlimited && t("unlimited")}
          {isGB && (
            <>
              <span className="pr-2xs">
                {(currentTransfer?.quota as number) / BYTES_IN_GB}
              </span>
              {t("GB")}
            </>
          )}
          {isNan && t("n/a")}
        </Col>
      </Row>

      <Row className="mt-m mb-m">
        <Col className="ams-transfer-stats__key">{t("nextRotation")}</Col>
        <Col className="ams-col__value">
          <span className="pr-s">
            {nextRotation
              ? getMonthDayYearDate(new Date(nextRotation))
              : t("n/a")}
          </span>
          {isManualRecycleAvailable && (
            <ButtonText
              className="ams-button__manual-recycle mb-0 pa-0"
              disabled={isManualRecycleDisabled}
              onClick={onRecycle}
            >
              {t("manualRecycle")}
            </ButtonText>
          )}
        </Col>
      </Row>

      {!transferLimitUnlimited && currentTransfer && (
        <Row className="mt-m mb-m">
          <Col className="ams-transfer-stats__key">
            {t("dataUsedThisRotation")}
          </Col>
          <Col className="ams-col__value ams-col__in-gb">
            <span>{amountInGB}</span>
            <span className="pr-2xs">{t("GB")}</span>
            {t("of")}
            <span className="pl-2xs">{quotaInGB}</span>
            <span>{t("GB")}</span>
          </Col>
        </Row>
      )}

      <Row className="mt-m mb-m">
        <Col className="ams-transfer-stats__key ams-transfer-stats__autorecycle">
          {t("autorecycle")}

          <Tooltip
            showOnClick
            placement="right"
            skidding={30}
            activator={<Icon>{"help" as IconName}</Icon>}
          >
            {t("whenYourDataLimitIsReached")}
          </Tooltip>
        </Col>

        <Col className="ams-col__value ams-autorecycle">
          <Switch
            checked={isAutorecycle}
            disabled={isFetchingSubscriptionOptions}
            onChange={handleChangeAutoRecycle}
          />
          <span
            className={classnames("pl-2xs", {
              "ams-autorecycle--success": isAutorecycle,
            })}
          >
            {isAutorecycle ? t("on") : t("off")}
          </span>
        </Col>
      </Row>

      <Row className="mt-m mb-m">
        <Col className="ams-transfer-stats__key">{t("lifetimeTransfer")}</Col>
        <Col className="ams-col__value">
          {lifetimeTransfer
            ? `${lifetimeTransfer / BYTES_IN_GB} ${t("GB")}`
            : t("n/a")}
        </Col>
      </Row>
    </Grid>
  );
};
